import { triggerWords, useFeedback } from './feedback-context.ts';
import { useMe } from '../session.tsx';
import { Button, HowSheet, HowWord } from './ui.tsx';

/**
 * The notice at the top of every page while the portal is under development
 * (Jason, 2026-10-06, before sharing it with a client: "a very visible banner
 * at the top of the application that says 'this application is under
 * development and there might be issues' with a link that shows a little help
 * pop up that instructs on how to report bugs or make feature suggestions").
 * A bengara band, the same for NSB and a client: the sentence in Newsreader
 * and one link as a label. The link opens how to report: four steps, each
 * with the thing itself drawn small as it looks on screen (the button, the
 * two choices, the sheet with its picture marked, Send; Jason: "show actual
 * examples in the images"), and the two ways in as buttons that open the
 * feedback sheet already on the right choice. Meanwhile the button at the
 * bottom right of every screen, named by the words the reader sees on it, is
 * pointed at (`FeedbackTrigger`): it stays above the backdrop, ringed, with an
 * arrow to it. The dialog sits at the top, leaving the foot of the window to
 * that button at every width.
 */
export function DevNotice() {
  const { data: me } = useMe();
  const { open, how, showHow } = useFeedback();
  return (
    <>
      <NoticeBand onHow={() => showHow(true)} />
      {how ? (
        <HowToReport
          button={triggerWords(me)}
          signedIn={Boolean(me)}
          onReport={(issueType) => open('issue', undefined, issueType)}
          onClose={() => showHow(false)}
        />
      ) : null}
    </>
  );
}

/** The band itself: the sentence, and the link that opens how to report. */
export function NoticeBand({ onHow }: { onHow: () => void }) {
  return (
    <div className="bg-alert text-paper" role="region" aria-label="Under development">
      <div className="shell flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 py-2.5">
        <p className="font-display text-[1.0625rem] leading-snug sm:text-[1.125rem]">This application is under development, and there might be issues.</p>
        <button
          type="button"
          onClick={onHow}
          className="label hit min-h-11 !text-paper !text-[0.75rem] underline decoration-paper/50 decoration-1 underline-offset-[0.3em] hover:decoration-paper sm:min-h-0"
        >
          How to report a bug or suggest a feature
        </button>
      </div>
    </div>
  );
}

/**
 * How to report: four steps, each shown as it looks on screen and said in a
 * line, two across on a wide screen, and the two ways in as buttons. Esc or a
 * click outside closes it. Its backdrop lies under the button at the bottom
 * right, which is pointed at and can be pressed. Signed out (the sign-in
 * page), the steps stand alone: the button is there once signed in.
 */
export function HowToReport({
  button,
  signedIn,
  onReport,
  onClose,
}: {
  button: string;
  signedIn: boolean;
  onReport: (issueType: 'bug' | 'feature') => void;
  onClose: () => void;
}) {
  return (
    <HowSheet
      title="Reporting a bug or suggesting a feature"
      line="The portal is still being built. Anything broken or missing is worth a report, and only NSB sees it."
      onClose={onClose}
      steps={[
        {
          example: <ExampleButton button={button} />,
          text: (
            <>
              Press <HowWord>{button}</HowWord>, at the bottom right of every screen{signedIn ? '' : ', once signed in'}.
            </>
          ),
        },
        { example: <ExampleChoice />, text: 'Choose a bug or a feature.' },
        { example: <ExampleSheet />, text: 'Write what happened. Mark the picture of the screen, or leave it out.' },
        { example: <ExampleSend />, text: 'Send.' },
      ]}
    >
      {signedIn ? (
        <div className="mt-7 flex flex-wrap items-center gap-4">
          <Button onClick={() => onReport('bug')}>Report a bug</Button>
          <Button tone="quiet" onClick={() => onReport('feature')}>
            Suggest a feature
          </Button>
        </div>
      ) : null}
    </HowSheet>
  );
}

/*
 * The examples: the real things, small. Each is laid out with the same classes
 * as the thing it shows (the trigger and the sheet in feedback.tsx), so it
 * stays true to what the reader will find. None of it can be pressed.
 */

/** A corner of a screen, its words faint, and the button as it sits there. */
function ExampleButton({ button }: { button: string }) {
  return (
    <div className="relative h-24 w-full max-w-[16rem] rounded-[3px] border border-rule-strong bg-sheet">
      <div className="absolute left-4 top-4 space-y-2">
        <div className="h-1.5 w-20 rounded-sm bg-rule" />
        <div className="h-1.5 w-32 rounded-sm bg-rule" />
        <div className="h-1.5 w-14 rounded-sm bg-rule" />
      </div>
      <span className="label absolute bottom-3 right-3 rounded-[3px] border border-alert bg-sheet px-3 py-2 !text-ink shadow-[0_6px_18px_rgba(20,22,19,.08)] ring-2 ring-alert ring-offset-2 ring-offset-sheet">
        {button}
      </span>
    </div>
  );
}

/** The two choices, in the sheet's own words. */
function ExampleChoice() {
  return (
    <div className="w-full max-w-[18rem] divide-y divide-rule border-y border-rule">
      <div className="px-1 py-2">
        <span className="block text-[0.9375rem]">The app isn’t working right</span>
        <span className="block text-[0.75rem] text-ink-3">About the software: private to NSB.</span>
      </div>
      <div className="px-1 py-2">
        <span className="block text-[0.9375rem]">Suggest a feature</span>
        <span className="block text-[0.75rem] text-ink-3">Something the portal should do.</span>
      </div>
    </div>
  );
}

/** The sheet: the picture of the screen with a mark drawn on it, and the words beside. */
function ExampleSheet() {
  return (
    <div className="grid h-28 w-full max-w-[18rem] grid-cols-2 gap-2">
      <div className="relative overflow-hidden rounded-[3px] border border-rule-strong bg-inset">
        <div className="absolute left-2 top-2 space-y-1.5">
          <div className="h-1 w-10 rounded-sm bg-rule-strong" />
          <div className="h-1 w-16 rounded-sm bg-rule-strong" />
          <div className="h-1 w-12 rounded-sm bg-rule-strong" />
        </div>
        <div className="absolute bottom-3 right-3 h-5 w-10 rounded-[2px] border border-rule-strong bg-sheet" />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
          <ellipse cx="68" cy="74" rx="26" ry="16" fill="none" className="stroke-alert" strokeWidth={2.2} vectorEffect="non-scaling-stroke" />
        </svg>
      </div>
      <div className="rounded-[2px] border border-rule-strong bg-paper p-2 text-[0.75rem] leading-snug text-ink-3">What happened, and what did you expect?</div>
    </div>
  );
}

/** Send, as the sheet has it. */
function ExampleSend() {
  return (
    <div className="flex items-center gap-5">
      <span className="rounded-[3px] bg-ink px-5 py-2.5 text-[0.9375rem] text-paper">Send</span>
      <span className="text-[0.9375rem] text-ink-2">Back</span>
    </div>
  );
}
